import { Link } from '@inertiajs/react';

import Icon from '@/components/public/ui/Icon';
import Seo from '@/components/public/Seo';
import PageHero from '@/components/public/sections/PageHero';
import { parishName, parishEyebrow, useSite } from '@/hooks/useSite';
import { trackUmamiEvent } from '@/lib/analytics';

const sectionLabel =
  'text-xs font-bold tracking-widest uppercase text-primary \
  mb-3 block';

export default function Whistleblowing() {
  const site = useSite();
  const name = parishName(site);
  const hq = site.locations.find((l) => l.headquarters) ?? site.locations[0];
  const pageBadge = 'Transparência';
  const pageTitle = 'Canal de Denúncias.';
  const pageAccentWord = 'Denúncias.';
  const pageSubtitle =
    'Canal interno para comunicação de infrações, em \
    cumprimento da Lei n.º 93/2021, de 20 de dezembro, que estabelece o regime \
    geral de proteção de denunciantes.';

  const baseClassName = 'max-w-7xl mx-auto px-4 md:px-8 py-16';

  return (
    <>
      <Seo
        title="Canal de Denúncias"
        description="Canal interno para comunicação de infrações, em cumprimento da Lei n.º 93/2021, que estabelece o regime geral de proteção de denunciantes."
      />
      <PageHero
        badge={pageBadge}
        breadcrumbs={[
          { label: 'Início', href: '/' },
          { label: 'Transparência' },
          { label: 'Canal de Denúncias' },
        ]}
        title={pageTitle}
        accentWord={pageAccentWord}
        subtitle={pageSubtitle}
      />
      <div className={baseClassName}>
        <div className="mx-auto max-w-4xl space-y-6">
          {/* Submission CTA — primary entry point to the formal channel. */}
          <Link
            href="/transparency/whistleblowing/new"
            onClick={() => trackUmamiEvent('whistleblowing_start', { source: 'top_cta' })}
            className="surface-card text-on-surface group hover:border-line-strong flex items-center gap-4 rounded-xl p-5 transition-all hover:shadow-md md:p-6"
          >
            <span
              className="bg-primary/[0.12] flex h-12 w-12 shrink-0 items-center justify-center rounded-xl"
              aria-hidden="true"
            >
              <span className="text-2xl leading-none">⚖️</span>
            </span>
            <span className="min-w-0 flex-1">
              <span className="text-on-surface block text-base font-bold md:text-lg">
                Submeter denúncia
              </span>
              <span className="text-on-surface-light block text-sm">
                Formulário confidencial · pode omitir contacto · seguimento no prazo legal
              </span>
            </span>
            <span className="text-primary inline-flex items-center gap-1 text-sm font-semibold">
              Abrir
              <Icon name="arrow_forward" size="sm" />
            </span>
          </Link>

          <article className="surface-card text-on-surface-light space-y-8 p-6 leading-relaxed md:p-10">
            <section>
              <span className={sectionLabel}>1 — Enquadramento Legal</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Base jurídica
              </h2>
              <p>
                A Lei n.º 93/2021, de 20 de dezembro, transpõe a Diretiva (UE) 2019/1937 e
                estabelece o regime geral de proteção de denunciantes de infrações. Obriga as
                entidades públicas, incluindo as autarquias locais, a disponibilizar canais internos
                para a receção e o tratamento de denúncias.
              </p>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>2 — Quem pode denunciar</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Âmbito subjetivo
              </h2>
              <p>
                Pode apresentar denúncia a pessoa singular que tenha conhecimento de infração no
                âmbito da sua atividade profissional ou relação com a entidade, nos termos da Lei
                n.º 93/2021, designadamente:
              </p>
              <ul className="mt-3 ml-5 list-outside list-disc space-y-2">
                <li>
                  Trabalhadores da {parishEyebrow(site).toLowerCase()} (eleitos, dirigentes,
                  funcionários, prestadores de serviços).
                </li>
                <li>Candidatos a emprego, estagiários e voluntários.</li>
                <li>Trabalhadores de fornecedores, prestadores de serviços ou subcontratantes.</li>
                <li>
                  Pessoas que tenham conhecimento direto de uma infração no âmbito de uma relação
                  profissional com a Junta.
                </li>
              </ul>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>3 — O que pode ser comunicado</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Infrações abrangidas
              </h2>
              <ul className="mt-3 ml-5 list-outside list-disc space-y-2">
                <li>
                  Corrupção e infrações conexas (suborno, peculato, recebimento indevido de
                  vantagem).
                </li>
                <li>Conflitos de interesses e incompatibilidades.</li>
                <li>Irregularidades em procedimentos de contratação pública.</li>
                <li>Gestão financeira ou patrimonial irregular.</li>
                <li>Atos lesivos dos interesses financeiros da União Europeia.</li>
                <li>Violações em matéria de proteção do ambiente, saúde pública ou segurança.</li>
                <li>Violações da proteção de dados pessoais.</li>
              </ul>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>4 — Como denunciar</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Canais disponíveis
              </h2>
              <p>
                As denúncias podem ser apresentadas, por escrito ou verbalmente, pelos seguintes
                meios:
              </p>
              <ul className="mt-3 ml-5 list-outside list-disc space-y-2">
                <li>
                  <span className="text-on-surface font-medium">Formulário online:</span>{' '}
                  <Link
                    href="/transparency/whistleblowing/new"
                    onClick={() => trackUmamiEvent('whistleblowing_start', { source: 'body_link' })}
                    className="text-primary font-medium hover:underline"
                  >
                    /transparency/whistleblowing/new
                  </Link>{' '}
                  (canal recomendado). Pode ser submetido sem indicar dados de identificação ou
                  contacto.
                </li>
                <li>
                  <span className="text-on-surface font-medium">
                    Correio postal em sobrescrito fechado:
                  </span>{' '}
                  {name} — Canal de Denúncias{hq?.address ? ` — ${hq.address}` : ''}.
                </li>
                <li>
                  <span className="text-on-surface font-medium">Reunião presencial:</span> mediante
                  pedido prévio dirigido à pessoa ou equipa responsável pelo tratamento do canal,
                  com agendamento no prazo máximo de 15 dias.
                </li>
              </ul>
              <p className="mt-3">
                A denúncia deve incluir, na medida do possível, a identificação dos factos, o
                período em que ocorreram, os intervenientes e os elementos de prova disponíveis.
              </p>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>5 — Confidencialidade e proteção</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Garantias ao denunciante
              </h2>
              <p>
                A identidade do denunciante é tratada de forma confidencial e só pode ser divulgada
                nos casos legalmente previstos, com as garantias e notificações legalmente
                aplicáveis. Quando estejam preenchidos os pressupostos legais, os denunciantes
                beneficiam das seguintes garantias:
              </p>
              <ul className="mt-3 ml-5 list-outside list-disc space-y-2">
                <li>Proibição de retaliação, direta ou indireta, em razão da denúncia.</li>
                <li>
                  Presunção de que qualquer ato lesivo ocorrido até dois anos após a denúncia
                  constitui retaliação, salvo prova em contrário.
                </li>
                <li>Acesso às medidas de proteção e apoio previstas na lei, quando aplicável.</li>
                <li>
                  Inversão do ónus da prova em procedimentos judiciais ou disciplinares
                  relacionados.
                </li>
              </ul>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>6 — Tratamento e prazos</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Como respondemos
              </h2>
              <ul className="mt-3 ml-5 list-outside list-disc space-y-2">
                <li>
                  Acusação de receção da denúncia no prazo legal, quando exista meio de contacto.
                </li>
                <li>Análise dos factos e abertura de inquérito interno, quando aplicável.</li>
                <li>
                  Comunicação das medidas previstas ou adotadas e dos respetivos fundamentos, no
                  prazo legal aplicável.
                </li>
                <li>
                  Encaminhamento para as autoridades competentes (Ministério Público, MENAC, IGF)
                  sempre que os factos o exijam.
                </li>
              </ul>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>7 — Canais externos</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Alternativas ao canal interno
              </h2>
              <p>
                O denunciante pode optar por recorrer diretamente a um canal externo, designadamente
                o Mecanismo Nacional Anticorrupção (MENAC) em{' '}
                <a
                  href="https://menac.pt"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackUmamiEvent('external_link_click', {
                      target: 'https://menac.pt',
                      label: 'MENAC',
                      source: 'whistleblowing',
                    })
                  }
                  className="text-primary font-medium hover:underline"
                >
                  www.menac.pt
                </a>
                , ou às autoridades judiciárias, sempre que existam motivos para crer que a denúncia
                interna não terá seguimento adequado ou implica risco de retaliação.
              </p>
            </section>
          </article>
        </div>
      </div>
    </>
  );
}
